import { yupResolver } from '@hookform/resolvers/yup';
import { CountryCode } from 'libphonenumber-js';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'utils/general/storage';

import {
  useCheckIdentifier,
  useLoginAuthntication,
} from '@/modules/auth/apiHooks';
import { useLoginFunctions } from '@/modules/auth/components/FormsContainer/Login/logic';
import Header from '@/modules/auth/components/Header';
import { IAuth } from '@/modules/auth/types';
import {
  Button,
  Checkbox,
  InputField,
  PhoneInputWithCountry,
} from '@/shared/components/atoms';
import Tabs from '@/shared/components/atoms/Tabs';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';

import Eye from '~/images/login/Eye.svg';
import EyeSlash from '~/images/login/EyeSlash.svg';

export const TAB_QUERY_INDICATOR = 'loginType';

const Login = () => {
  const { t } = useTranslation('login');

  const { query, push } = useRouter();
  const loginType = query[TAB_QUERY_INDICATOR] as 'phoneNumber' | 'email';

  const { login } = useAuth();
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [keepMeLoggedIn, setKeepMeLoggedIn] = useState<boolean>(true);

  const { getCookie } = useCookies();
  const countryCode = useMemo(
    () => getCookie('defaultCountryCode') ?? 'EG',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const {
    mutate: loginAuthntication,
    isLoading: isLoginLoading,
    data: loginAuthnticationData,
  } = useLoginAuthntication();

  const {
    mutate: checkIdentifier,
    isLoading: isCheckIdentifierLoading,
    data: checkIdentifierData,
  } = useCheckIdentifier();

  const { LoginFormValidationSchema } = useLoginFunctions(
    loginType,
    !!checkIdentifierData
  );

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      identifier: '',
      password: '',
      country: countryCode,
    } as IAuth.ILoginSchema,
    resolver: yupResolver(LoginFormValidationSchema),
  });

  const {
    register,
    control,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = methods;

  /** Authentication flow:
   * 1. Check if the identifier (phone number or email) has an associated password
   * 2. If a password exists:
   *    - Display the password input field
   *    - Proceed with normal login flow
   * 3. If no password exists:
   *    - Redirect to the OTP (One-Time Password) page
   *    - If OTP is verified successfully:
   *      - Redirect to the create password page
   *      - If password creation is successful, redirect to the login page
   *      - If password creation fails, redirect to the login page with an error message
   * 4. Handle any errors that occur during this process and provide appropriate user feedback
   */
  const onSubmit = () => {
    const identifier = getValues('identifier');
    const country = getValues('country');
    const password = getValues('password');

    if (loginType === 'phoneNumber') {
      if (checkIdentifierData === undefined) {
        checkIdentifier(getValues('identifier'), {
          onSuccess: (response) => {
            if (!response?.data?.hasPassword) {
              push({
                pathname: routes.loginOtp,
                query: {
                  identifier,
                  country,
                },
              });
            }
          },
        });
      } else {
        loginAuthntication({
          identifier,
          password,
        });
      }
    } else {
      loginAuthntication({
        identifier,
        password,
      });
    }
  };

  useEffect(() => {
    if (loginAuthnticationData) {
      login(
        loginAuthnticationData.token,
        loginAuthnticationData.user ?? undefined,
        keepMeLoggedIn
          ? loginAuthnticationData.expiry ?? 1727202181651
          : 86400000
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginAuthnticationData]);

  useEffect(() => {
    setValue('identifier', '');
    setValue('password', '');
    setValue('country', countryCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginType]);

  return (
    <FormProvider {...methods}>
      <form className='flex flex-col gap-16'>
        <Header title={t('welcome-back')} />
        <Tabs
          tabsList={[
            { label: t('mobile-number'), key: 'phoneNumber' },
            { label: t('email-address'), key: 'email' },
          ]}
          getLabel={(tab) => tab.label}
          getValue={(tab) => tab.key}
          variant='underline'
          tabQueryIndicator={TAB_QUERY_INDICATOR}
          containerClassName='w-full'
          tabClasseName='text-14 font-medium'
          defaultValue='phoneNumber'
        />

        {loginType === 'phoneNumber' && (
          <div className='mt-16 flex flex-col gap-0'>
            <PhoneInputWithCountry
              label={t('mobile-number')}
              width='w-full'
              id='identifier'
              defaultCountry={getValues('country') as CountryCode}
              name='identifier'
              control={
                control as Control<FieldValues> | Control<IAuth.ILoginSchema>
              }
              placeholder={t('enter-mobile-number')}
              required
              errorMessage={
                getValues('identifier') === null
                  ? t('this-field-is-required')
                  : errors?.identifier?.message
              }
              className='rounded-xl bg-background-transparent py-2'
              borderColor='border-outline-variant border-xs'
              onCountryChange={(country) => setValue('country', country)}
            />
          </div>
        )}
        {loginType === 'email' && (
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-14'
            label={t('email-address')}
            placeholder='you@exmple.com'
            error={!!errors.identifier}
            errorMessage={errors.identifier?.message}
            className='!rounded-xl bg-background-transparent'
            {...register('identifier')}
          />
        )}
        {/* Display password field if:
         1. Login type is email
         2. Login type is phone number and user has a password */}
        {((loginType === 'phoneNumber' &&
          checkIdentifierData?.data?.hasPassword) ||
          loginType === 'email') && (
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-14'
            label={t('password')}
            placeholder='••••••••'
            error={!!errors.password}
            className='!rounded-xl bg-background-transparent'
            type={passwordVisible ? 'text' : 'password'}
            suffix={
              <div
                className='cursor-pointer'
                onClick={() => setPasswordVisible((prev) => !prev)}
              >
                {passwordVisible ? (
                  <Eye width='24' height='24' />
                ) : (
                  <EyeSlash width='24' height='24' />
                )}
              </div>
            }
            {...register('password')}
          />
        )}
        <div className='flex w-full items-center justify-between'>
          <div className='flex items-center gap-2'>
            <Checkbox
              checked={keepMeLoggedIn}
              onChange={() => {
                setKeepMeLoggedIn((prev) => !prev);
              }}
            />
            <span className='text-12 font-regular text-text-default'>
              {t('keep-me-logged-in')}
            </span>
          </div>
          <div
            className='cursor-pointer text-12 font-bold text-primary-400 underline'
            onClick={() => {
              push({
                pathname: routes.forgotPassword,
              });
            }}
          >
            {t('forgot-password')}
          </div>
        </div>
        <div className='flex flex-col gap-32'>
          <Button
            size='full'
            type='submit'
            onClick={handleSubmit(onSubmit)}
            className='mt-32 w-full !rounded-xl py-14 text-base font-semi-bold'
            disabled={isLoginLoading || isCheckIdentifierLoading}
          >
            {t('login')}
          </Button>
          <div className='flex w-full items-center justify-center gap-1'>
            <span className='text-14 font-medium text-surface-10'>
              {t('no-account')}
            </span>
            <span
              className='cursor-pointer text-14 font-semi-bold text-primary-default underline'
              onClick={() => {
                push(routes.signup);
              }}
            >
              {t('sign-up')}
            </span>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Login;
